import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SellerFormData } from "@/types/seller-registration.types";
import { SellerStepOne } from "@/components/seller-registration/steps/SellerStepOne";
import { SellerStepTwo } from "@/components/seller-registration/steps/SellerStepTwo";
import { SellerStepThree } from "@/components/seller-registration/steps/SellerStepThree";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SELLER_TRANSLATIONS } from "@/constants/seller-registration.translations";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from "firebase/auth";
import { collection, doc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { sendWelcomeEmail } from "@/lib/send-welcome-email";
import {
  addRoleToCurrentUser,
  assignRoleViaNestJS,
  EMAIL_ALREADY_IN_USE_ROLE_MESSAGE,
  getErrorMessage,
  isEmailAlreadyInUseError,
} from "@/lib/registration-roles";

const TOTAL_STEPS = 3;
const STORAGE_KEY = "seller_registration_draft";

const getInitialFormData = (): SellerFormData => ({
  currentStep: 1,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  howHeard: [],
  companyName: "",
  industry: "",
  yearEstablished: "",
  jurisdictionsCanada: [],
  jurisdictionsUSA: [],
  jurisdictionsEurope: [],
  jurisdictionsAustralia: [],
  addressSearch: "",
  companyStreet: "",
  companyCity: "",
  companyProvince: "",
  companyZipCode: "",
  annualRevenue: 0,
  ebitda: 0,
  askingPrice: 0,
  positiveCashFlow: "",
  doc3YearsPL: false,
  doc3YearsTax: false,
  docRecentBalance: false,
  documentsReady: "",
  firstSale: "",
  needGuidance: "",
  termsAccepted: false,
});

const RegisterSellers: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

// Capture Deal Sourcer referral
useEffect(() => {
  const sourcer = searchParams.get('sourcer');
  if (sourcer) {
    try {
      localStorage.setItem('dealSourcerRef', sourcer);
    } catch {}
  }
}, [searchParams]);

// Capture Broker→Seller invitation link (same pattern as dealSourcerRef above)
useEffect(() => {
  const repId = searchParams.get('repId');
  const brokerId = searchParams.get('brokerId');
  if (repId && brokerId) {
    try {
      localStorage.setItem('sellerRepId', repId);
      localStorage.setItem('sellerBrokerId', brokerId);
    } catch {}
  }
}, [searchParams]);
  const { language } = useLanguage();
  const t = SELLER_TRANSLATIONS[language];

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<SellerFormData>(getInitialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Load saved data
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(parsed.formData || getInitialFormData());
        setCurrentStep(parsed.step || 1);
      } catch (e) {
        console.error("Error loading saved form data", e);
      }
    }
  }, []);

  // Save data on change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, step: currentStep }));
  }, [formData, currentStep]);

  const updateFormData = useCallback((updates: Partial<SellerFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = t.errors.firstNameRequired;
      if (!formData.lastName.trim()) newErrors.lastName = t.errors.lastNameRequired;
      if (!formData.email.trim()) newErrors.email = t.errors.emailRequired;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t.errors.emailInvalid;
      if (!formData.phone.trim()) newErrors.phone = t.errors.phoneRequired;
      if (!formData.password) newErrors.password = t.errors.passwordRequired;
      else if (formData.password.length < 6) newErrors.password = t.errors.passwordMinLength;
      if (!formData.confirmPassword) newErrors.confirmPassword = t.errors.confirmPasswordRequired;
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t.errors.passwordMismatch;
      if (formData.howHeard.length === 0) newErrors.howHeard = t.errors.howHeardRequired;
    }

    if (step === 2) {
      if (!formData.companyName.trim()) newErrors.companyName = t.errors.companyNameRequired;
      if (!formData.industry) newErrors.industry = t.errors.industryRequired;
      if (!formData.yearEstablished) newErrors.yearEstablished = t.errors.yearEstablishedRequired;
      else {
        const year = parseInt(formData.yearEstablished);
        if (year > 2022) newErrors.yearEstablished = t.errors.yearEstablishedInvalid;
      }
      if (!formData.companyStreet.trim()) newErrors.companyStreet = t.errors.addressRequired;
      if (!formData.companyCity.trim()) newErrors.companyCity = t.errors.cityRequired;
      if (!formData.companyProvince.trim()) newErrors.companyProvince = t.errors.provinceRequired;
      if (!formData.companyZipCode.trim()) newErrors.companyZipCode = t.errors.zipCodeRequired;
      if (!formData.positiveCashFlow) newErrors.positiveCashFlow = t.errors.positiveCashFlowRequired;
      else if (formData.positiveCashFlow === "no") newErrors.positiveCashFlow = t.errors.positiveCashFlowNo;

      // Documents: must have (P&L + Balance Sheet) OR (Tax Reports)
      const hasPLAndBalance = formData.doc3YearsPL && formData.docRecentBalance;
      const hasTaxReports = formData.doc3YearsTax;
      if (!hasPLAndBalance && !hasTaxReports) {
        newErrors.documents = t.errors.documentsRequired;
      }
      if (!formData.documentsReady) newErrors.documentsReady = t.errors.documentsReadyRequired;
      if (!formData.firstSale) newErrors.firstSale = t.errors.firstSaleRequired;
      if (!formData.needGuidance) newErrors.needGuidance = t.errors.needGuidanceRequired;
    }

    if (step === 3) {
      if (!formData.termsAccepted) newErrors.termsAccepted = t.step3.termsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      const sellerProfile = {
        email: formData.email,
        displayName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || "",
        companyName: formData.companyName || "",
        industry: formData.industry || "",
        yearEstablished: formData.yearEstablished || "",
        jurisdictions: {
          canada: formData.jurisdictionsCanada || [],
          usa: formData.jurisdictionsUSA || [],
          europe: formData.jurisdictionsEurope || [],
          australia: formData.jurisdictionsAustralia || [],
        },
        address: {
          street: formData.companyStreet || "",
          city: formData.companyCity || "",
          province: formData.companyProvince || "",
          zipCode: formData.companyZipCode || "",
        },
        annualRevenue: formData.annualRevenue || 0,
        ebitda: formData.ebitda || 0,
        askingPrice: formData.askingPrice || 0,
        positiveCashFlow: formData.positiveCashFlow || "",
        doc3YearsPL: formData.doc3YearsPL,
        doc3YearsTax: formData.doc3YearsTax,
        docRecentBalance: formData.docRecentBalance,
        documentsReady: formData.documentsReady || "",
        firstSale: formData.firstSale || "",
        needGuidance: formData.needGuidance || "",
        howHeard: formData.howHeard || [],
        termsAccepted: formData.termsAccepted,
        status: "pending_review",
        language: language,
        updatedAt: serverTimestamp(),
      };

      try {
        // 1. Create Firebase Auth user
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;

        await signInWithEmailAndPassword(auth, formData.email, formData.password);

        await updateProfile(user, {
          displayName: `${formData.firstName} ${formData.lastName}`,
        });

        // 2. Save profile to Firestore
        // Récupérer le sourcer_uid si présent
const sourcerUid = localStorage.getItem('dealSourcerRef');

await setDoc(doc(db, "users", user.uid), {
            ...sellerProfile,
            roles: ["seller"],
            createdAt: serverTimestamp(),
            ...(sourcerUid ? { sourcedBy: sourcerUid } : {}),
          },
          { merge: true },
        );

        await assignRoleViaNestJS(user, "seller");

        // Lien Broker→Vendeur : si ce compte vient d'un lien d'invitation broker
        // (repId/brokerId), on complète l'enregistrement representedSellers en
        // attente avec le vrai UID, on active hasPortalAccess, et on relie le
        // calendrier fusionné. Même pattern que dealSourcerRef ci-dessus.
               const sellerRepId = localStorage.getItem('sellerRepId');
        const sellerBrokerId = localStorage.getItem('sellerBrokerId');
        if (sellerRepId && sellerBrokerId) {
          try {
            await updateDoc(doc(db, "representedSellers", sellerRepId), {
              uid: user.uid,
              hasPortalAccess: true,
              linkedAt: serverTimestamp(),
            });
            // Retrouver le mergedCalendar créé par le broker pour ce sellerRepId
            // (créé avec sellerId = sellerRepId au moment de addRepresentedSeller)
            const calendarsQuery = query(
              collection(db, "mergedCalendars"),
              where("brokerId", "==", sellerBrokerId),
              where("sellerId", "==", sellerRepId),
            );
            const calendarsSnapshot = await getDocs(calendarsQuery);
            if (!calendarsSnapshot.empty) {
              const calendarDoc = calendarsSnapshot.docs[0];
              await updateDoc(calendarDoc.ref, {
                sellerId: user.uid,
              });
            }
 
            // AJOUTÉ — filet de sécurité : corrige rétroactivement tout
            // deal soumis par le broker AVANT que ce vendeur n'ait créé
            // son compte. deals.service.ts submitDeal() ne peut résoudre
            // representedSellerId -> uid que si le vendeur est DÉJÀ lié
            // au moment de la soumission — dans le cas inverse (chemin
            // "Partager formulaire vierge", où le vendeur n'a pas encore
            // de compte quand le broker soumet), sellerId reste le repId
            // indéfiniment, bloquant silencieusement SellerDashboard.tsx,
            // CIMApprovalBanner, et les notifications email côté broker
            // (deal-approval.service.ts sendForApproval cherche
            // users/{sellerId} — un repId n'y correspond jamais).
            //
            // On cherche ici tous les dealSubmissions où sellerId ==
            // sellerRepId (le repId, pas encore résolu) et on les met à
            // jour vers le vrai uid, maintenant disponible. Non-bloquant :
            // un échec ici ne doit jamais empêcher la création du compte.
            try {
              const orphanDealsQuery = query(
                collection(db, "dealSubmissions"),
                where("sellerId", "==", sellerRepId),
              );
              const orphanDealsSnapshot = await getDocs(orphanDealsQuery);
              if (!orphanDealsSnapshot.empty) {
                const updates = orphanDealsSnapshot.docs.map((dealDoc) =>
                  updateDoc(dealDoc.ref, { sellerId: user.uid })
                );
                await Promise.all(updates);
                console.log(`[RegisterSellers] Backfilled sellerId on ${orphanDealsSnapshot.size} deal(s) previously linked to repId ${sellerRepId}`);
 
                // Miroir 'deals' — même correction, pour cohérence
                // d'affichage (BrokerDashboard.tsx My Listings, etc.)
                const orphanMirrorQuery = query(
                  collection(db, "deals"),
                  where("sellerId", "==", sellerRepId),
                );
                const orphanMirrorSnapshot = await getDocs(orphanMirrorQuery);
                if (!orphanMirrorSnapshot.empty) {
                  const mirrorUpdates = orphanMirrorSnapshot.docs.map((mirrorDoc) =>
                    updateDoc(mirrorDoc.ref, { sellerId: user.uid })
                  );
                  await Promise.all(mirrorUpdates);
                }
              }
            } catch (backfillError) {
              console.warn("[RegisterSellers] Failed to backfill orphan deals (non-blocking):", backfillError);
            }
 
            localStorage.removeItem('sellerRepId');
            localStorage.removeItem('sellerBrokerId');
          } catch (linkError) {
            // Ne bloque jamais la création de compte si le lien échoue —
            // le compte vendeur doit se créer même si l'association rate.
            console.warn("[RegisterSellers] Failed to link to representedSellers (non-blocking):", linkError);
          }
        }
 
      } catch (error: unknown) {
        if (!isEmailAlreadyInUseError(error)) throw error;
        await addRoleToCurrentUser({
          email: formData.email,
          password: formData.password,
          role: "seller",
          profileUpdates: sellerProfile,
        });
      }

      // 3. Send welcome email (fire-and-forget — must NOT block success screen)
      sendWelcomeEmail({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: "seller",
        language,
      }).catch((emailErr) => {
        console.warn("[RegisterSellers] sendWelcomeEmail failed (non-blocking):", emailErr);
      });

      localStorage.removeItem(STORAGE_KEY);
      setIsSuccess(true);
      toast.success(t.success.title);
    } catch (error: unknown) {
      if (isEmailAlreadyInUseError(error)) {
        toast.error(EMAIL_ALREADY_IN_USE_ROLE_MESSAGE);
      } else {
        toast.error(getErrorMessage(error, "An unexpected error occurred"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Optionnel : notifier le broker que son vendeur a complété son inscription
// (nécessite le contenu réel de sendWelcomeEmail / une Cloud Function de
// notification existante pour matcher le style — voir sendSellerInviteEmail.ts)

  const renderCurrentStep = () => {
    const props = { formData, updateFormData, errors, clearError, t };
    switch (currentStep) {
      case 1:
        return <SellerStepOne {...props} />;
      case 2:
        return <SellerStepTwo {...props} />;
      case 3:
        return <SellerStepThree {...props} />;
      default:
        return null;
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <div className="max-w-lg mx-auto bg-white border border-slate-200 p-10 rounded-xl text-center shadow-xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#1D314D] mb-4">{t.success.title}</h2>
          <p className="text-slate-600 mb-4">{t.success.message}</p>
          <p className="text-slate-500 mb-6">{t.success.emailConfirmation}</p>
          <Button onClick={() => navigate("/")} className="bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90 font-bold">
            {t.success.goToLogin}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Header Card - Navy */}
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <h2 className="text-2xl font-bold text-white text-center mb-1">{t.pageTitle}</h2>
          <p className="text-sm text-white/70 text-center">{t.stepsFlow}</p>

          {/* Step Indicator */}
          <div className="mt-6 flex justify-between gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`flex-1 py-2 rounded-lg text-center text-sm font-semibold transition-all ${
                  step < currentStep
                    ? "bg-[#CEAB65] text-[#1D314D]"
                    : step === currentStep
                      ? "bg-white text-[#1D314D]"
                      : "bg-white/20 text-white/70"
                }`}
              >
                {step}. {step === 1 ? t.steps.account : step === 2 ? t.steps.profile : t.steps.terms}
              </div>
            ))}
          </div>
          <p className="text-white/60 text-center mt-3 text-sm">
            {t.steps.name} {currentStep} {t.steps.of} {TOTAL_STEPS}
          </p>
        </div>

        {/* Form Content - Light */}
        <div className="bg-white border border-slate-200 rounded-b-xl p-6 md:p-8 shadow-lg">
          {renderCurrentStep()}

          {/* Navigation */}
          <div className="flex justify-between gap-4 mt-8 pt-6 border-t border-slate-200">
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                {t.nav.back}
              </Button>
            ) : (
              <div />
            )}

            {currentStep < TOTAL_STEPS ? (
              <Button
                type="button"
                onClick={handleNext}
                className="bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90 font-bold"
              >
                {t.nav.continue}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90 font-bold"
              >
                {isSubmitting ? t.nav.submitting : t.nav.submit}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-6">
        <p className="text-slate-500 text-sm">
          {t.alreadyMember}{" "}
          <a href="/login" className="text-[#1D314D] hover:text-[#CEAB65] font-medium">
            {t.login}
          </a>
        </p>
      </div>
    </div>
  );
};

export default RegisterSellers;
